import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { addCountryTranslation, getCountryTranslation, reloadTranslations, validateTranslationsFile, saveTranslationsToFile } from '../../services/translationsService';
import { 
  GlobeIcon, 
  SearchIcon, 
  FilterIcon,
  PlusIcon,
  EditIcon,
  TrashIcon,
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  SortAscIcon,
  SortDescIcon
} from 'lucide-react';

const Countries = () => {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();
  
  // دالة لترجمة اسم الدولة
  const translateCountryName = (arabicName, language) => {
    return getCountryTranslation(arabicName, language);
  };
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  const LIMIT_OPTIONS = [10, 20, 50, 100];
  const [isSearching, setIsSearching] = useState(false);
  
  // Add country modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newCountry, setNewCountry] = useState({
    code: '',
    translations: {
      fr: '',
      en: '',
      ar: ''
    }
  });
  const [formErrors, setFormErrors] = useState({});

  // Fetch countries data - API: /organization/countries?page=1&limit=10
  const fetchCountries = async (page = 1, search = '', itemsPerPage = limit) => {
    setLoading(true);
    try {
      const params = { page, limit: itemsPerPage };
      if (search.trim()) params.search = search.trim();
      const response = await api.get('/organization/countries', { params });

      if (response.data.success) {
        setCountries(response.data.data.countries);
        setTotalPages(response.data.data.pagination.pages);
        setTotal(response.data.data.pagination.total);
        setCurrentPage(response.data.data.pagination.page);
      } else {
        throw new Error(response.data.message || 'Failed to fetch countries');
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
      toast.error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch countries',
        {
          duration: 4000,
          position: 'top-center'
        }
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
    const isValid = validateTranslationsFile();
    if (isValid) {
      reloadTranslations();
    } else {
      console.error('Translations file is invalid');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // إعادة جلب البيانات عند تغيير اللغة
  useEffect(() => {
    if (currentLanguage) {
      fetchCountries(1, searchTerm);
    }
  }, [currentLanguage]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.length >= 2 || searchTerm.length === 0) {
        setIsSearching(true);
        fetchCountries(1, searchTerm).finally(() => setIsSearching(false));
      }
    }, 500);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Refetch when limit changes
  const isInitialMountCountries = useRef(true);
  useEffect(() => {
    if (isInitialMountCountries.current) {
      isInitialMountCountries.current = false;
      return;
    }
    fetchCountries(1, searchTerm, limit);
  }, [limit]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCountries(1, searchTerm);
  };

  const handlePageChange = (page) => {
    fetchCountries(page, searchTerm);
  };

  const clearFilters = () => {
    setSearchTerm('');
    fetchCountries(1, '', limit);
  };

  const getActiveFiltersCount = () => (searchTerm ? 1 : 0);

  // Handle add country
  const handleAddCountry = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = {};
    if (!newCountry.code.trim()) {
      errors.code = t('countries.codeRequired');
    } else if (newCountry.code.length !== 2) {
      errors.code = t('countries.codeLength');
    }
    
    // Validate translations
    if (!newCountry.translations.fr.trim()) {
      errors.translation_fr = t('countries.nameRequired_fr');
    }
    if (!newCountry.translations.en.trim()) {
      errors.translation_en = t('countries.nameRequired_en');
    }
    if (!newCountry.translations.ar.trim()) {
      errors.translation_ar = t('countries.nameRequired_ar');
    }
    
    setFormErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      return;
    }
    
    setIsAdding(true);
    try {
      const requestData = {
        name: newCountry.translations.fr.trim(), // إرسال الاسم الفرنسي إلى قاعدة البيانات
        code: newCountry.code.trim().toUpperCase()
      };
      
      // console.log('Sending data:', requestData); // للتأكد من البيانات
      
      const response = await api.post('/organization/countries', requestData);
      
      if (response.data.success) {
        // Save translations using the new system
        try {
          const translationResult = await addCountryTranslation(
            newCountry.translations.fr.trim(),
            {
              fr: newCountry.translations.fr.trim(),
              en: newCountry.translations.en.trim(),
              ar: newCountry.translations.ar.trim()
            }
          );

          if (translationResult.success) {
            console.log('Translation saved successfully:', translationResult.data);
            
            // Reload translations from file
            const updatedTranslations = reloadTranslations();
            console.log('Updated translations after save:', updatedTranslations);
            
            toast.success(response.data.message || t('countries.addSuccess'), {
              duration: 3000,
              position: 'top-center'
            });
          } else {
            toast.success(response.data.message || t('countries.addSuccess'), {
              duration: 3000,
              position: 'top-center'
            });
            console.warn('Country added but translation save failed:', translationResult.message);
          }
        } catch (translationError) {
          console.error('Translation save error:', translationError);
          toast.success(response.data.message || t('countries.addSuccess'), {
            duration: 3000,
            position: 'top-center'
          });
          console.warn('Country added but translation save failed');
        }
        
        // Reset form and close modal
        setNewCountry({ 
          code: '',
          translations: { fr: '', en: '', ar: '' }
        });
        setFormErrors({});
        setShowAddModal(false);
        
        // Refresh countries list
        fetchCountries(1, searchTerm, limit);
      } else {
        throw new Error(response.data.message || t('countries.addError'));
      }
    } catch (error) {
      console.error('Error adding country:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = t('countries.addError');
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        // معالجة أخطاء التحقق من صحة البيانات
        const validationErrors = error.response.data.errors;
        const errorMessages = Object.values(validationErrors).flat();
        errorMessage = errorMessages.join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, {
        duration: 5000,
        position: 'top-center'
      });
    } finally {
      setIsAdding(false);
    }
  };

  // Handle translation input changes
  const handleTranslationChange = (language, value) => {
    setNewCountry(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [language]: value
      }
    }));
    
    // Clear error when user starts typing
    const errorKey = `translation_${language}`;
    if (formErrors[errorKey]) {
      setFormErrors(prev => ({
        ...prev,
        [errorKey]: ''
      }));
    }
  };


  // Open add modal
  const openAddModal = () => {
    setNewCountry({ 
      code: '',
      translations: {
        fr: '',
        en: '',
        ar: ''
      }
    });
    setFormErrors({});
    setShowAddModal(true);
  };

  // Close add modal
  const closeAddModal = () => {
    setShowAddModal(false);
    setNewCountry({ 
      code: '',
      translations: {
        fr: '',
        en: '',
        ar: ''
      }
    });
    setFormErrors({});
  };

  // Handle export translations
  const handleExportTranslations = () => {
    const result = saveTranslationsToFile();
    if (result.success) {
      toast.success(result.message, {
        duration: 5000,
        position: 'top-center'
      });
    } else {
      toast.error(result.message, {
        duration: 3000,
        position: 'top-center'
      });
    }
  };

  // Handle status toggle
  const handleStatusToggle = async (countryId, currentStatus) => {
    try {
      const response = await api.put(`/countries/${countryId}/toggle-status`, {
        is_active: currentStatus === 1 ? 0 : 1
      });

      if (response.data.success) {
        toast.success(response.data.message || 'Status updated successfully', {
          duration: 3000,
          position: 'top-center'
        });
        fetchCountries(currentPage, searchTerm);
      } else {
        throw new Error(response.data.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(
        error.response?.data?.message || 
        error.message || 
        'Failed to update status',
        {
          duration: 4000,
          position: 'top-center'
        }
      );
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className={`p-6 ${isRTL ? 'font-arabic' : ''}`}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <GlobeIcon className={`h-8 w-8 text-primary-600 ${isRTL ? 'ml-3' : 'mr-3'}`} />
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <h1 className="text-2xl font-bold text-gray-900">
                {t('countries.title')}
              </h1>
              <p className="text-gray-600 mt-1">
                {t('countries.subtitle')}
              </p>
            </div>
          </div>
          <div className={`flex ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
            <button 
              onClick={openAddModal}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center"
            >
              <PlusIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('countries.addCountry')}
            </button>
            <button 
              onClick={handleExportTranslations}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
            >
              <GlobeIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              Export Translations
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters - Modern Design */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        <form onSubmit={handleSearch} className="p-4">
          <div className={`flex flex-col lg:flex-row gap-4 ${isRTL ? 'lg:flex-row-reverse' : ''}`}>
            <div className="flex-1 min-w-0">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('common.search')}</label>
              <div className="relative">
                <SearchIcon className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} />
                <input type="text" placeholder={t('countries.searchPlaceholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors`} />
                {isSearching && <div className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-3' : 'right-3'}`}><div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent" /></div>}
              </div>
            </div>
            <div className="lg:w-32">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('users.perPage')}</label>
              <select value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10))} className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors">
                {LIMIT_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div className="flex items-end gap-2 lg:flex-shrink-0">
              <button type="submit" className="px-4 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors">{t('common.search')}</button>
              {searchTerm && (
                <button type="button" onClick={clearFilters} title={t('common.clearFilters')} className="p-2.5 rounded-lg text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"><XCircleIcon className="h-5 w-5" /></button>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Countries Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
            <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <span className="ml-3 text-gray-600">{t('common.loading')}</span>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('countries.country')}
                    </th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('countries.code')}
                    </th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('countries.status')}
                    </th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('countries.createdAt')}
                    </th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('countries.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {countries.map((country) => (
                    <tr key={country.id} className="hover:bg-gray-50">
                      <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                              <GlobeIcon className="h-4 w-4 text-primary-600" />
                            </div>
                          </div>
                          <div className={`${isRTL ? 'mr-4' : 'ml-4'} ${isRTL ? 'text-right' : 'text-left'}`}>
                            <div className="text-sm font-medium text-gray-900">
                              {translateCountryName(country.name, currentLanguage || 'fr')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {country.code}
                        </span>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                        <button
                          onClick={() => handleStatusToggle(country.id, country.is_active)}
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            country.is_active === 1
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                          } transition-colors cursor-pointer`}
                        >
                          {country.is_active === 1 ? t('countries.active') : t('countries.inactive')}
                        </button>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {formatDate(country.created_at)}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                        <div className={`flex items-center ${isRTL ? 'justify-end space-x-reverse space-x-2' : 'justify-start space-x-2'}`}>
                          <button className="text-primary-600 hover:text-primary-900 p-1" title={t('countries.view')}>
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button className="text-blue-600 hover:text-blue-900 p-1" title={t('countries.edit')}>
                            <EditIcon className="h-4 w-4" />
                          </button>
                          <button className="text-red-600 hover:text-red-900 p-1" title={t('countries.delete')}>
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={`bg-white px-4 py-3 flex items-center ${isRTL ? 'justify-between flex-row-reverse' : 'justify-between'} border-t border-gray-200`}>
                <div className={`flex-1 flex ${isRTL ? 'justify-between flex-row-reverse' : 'justify-between'} sm:hidden`}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('countries.previous')}
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`${isRTL ? 'mr-3' : 'ml-3'} relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {t('countries.next')}
                  </button>
                </div>
                <div className={`hidden sm:flex-1 sm:flex sm:items-center ${isRTL ? 'sm:justify-between flex-row-reverse' : 'sm:justify-between'}`}>
                  <div className={isRTL ? 'text-right' : 'text-left'}>
                    <p className="text-sm text-gray-700">
                      {t('countries.showing')} <span className="font-medium">{((currentPage - 1) * limit) + 1}</span> {t('countries.to')}{' '}
                      <span className="font-medium">
                        {Math.min(currentPage * limit, total)}
                      </span>{' '}
                      {t('countries.of')} <span className="font-medium">{total}</span> {t('countries.results')}
                      {getActiveFiltersCount() > 0 && (
                        <span className={`${isRTL ? 'mr-2' : 'ml-2'} text-primary-600`}>
                          ({getActiveFiltersCount()} {t('countries.filtersApplied')})
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <nav className={`relative z-0 inline-flex rounded-md shadow-sm ${isRTL ? 'space-x-reverse -space-x-px' : '-space-x-px'}`}>
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 ${isRTL ? 'rounded-l-md' : 'rounded-r-md'} border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        <ChevronLeftIcon className="h-5 w-5" />
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === currentPage
                              ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 ${isRTL ? 'rounded-r-md' : 'rounded-l-md'} border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        <ChevronRightIcon className="h-5 w-5" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Country Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className={`flex items-center ${isRTL ? 'justify-between flex-row-reverse' : 'justify-between'} p-4 border-b border-gray-200`}>
              <h3 className={`text-lg font-medium text-gray-900 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('countries.addCountry')}
              </h3>
              <button
                onClick={closeAddModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleAddCountry} className="p-4">
              <div className="space-y-3">

                {/* Country Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('countries.code')} *
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={newCountry.code}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      setNewCountry(prev => ({ ...prev, code: value }));
                      if (formErrors.code) {
                        setFormErrors(prev => ({ ...prev, code: '' }));
                      }
                    }}
                    maxLength={2}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors.code ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder={t('countries.codePlaceholder')}
                  />
                  {formErrors.code && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.code}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {t('countries.codeHint')}
                  </p>
                </div>

                {/* Translation Fields */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* French Translation */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      🇫🇷 Français *
                    </label>
                    <input
                      type="text"
                      value={newCountry.translations.fr}
                      onChange={(e) => handleTranslationChange('fr', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        formErrors.translation_fr ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Ex: France"
                    />
                    {formErrors.translation_fr && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.translation_fr}</p>
                    )}
                  </div>

                  {/* English Translation */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      🇺🇸 English *
                    </label>
                    <input
                      type="text"
                      value={newCountry.translations.en}
                      onChange={(e) => handleTranslationChange('en', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        formErrors.translation_en ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Ex: France"
                    />
                    {formErrors.translation_en && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.translation_en}</p>
                    )}
                  </div>

                  {/* Arabic Translation */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      🇸🇦 العربية *
                    </label>
                    <input
                      type="text"
                      value={newCountry.translations.ar}
                      onChange={(e) => handleTranslationChange('ar', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        formErrors.translation_ar ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="مثال: فرنسا"
                    />
                    {formErrors.translation_ar && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.translation_ar}</p>
                    )}
                  </div>
                </div>

              </div>

              {/* Form Actions */}
              <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'} ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'} mt-4 pt-3 border-t border-gray-200`}>
                <button
                  type="button"
                  onClick={closeAddModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={isAdding}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isAdding}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isAdding ? (
                    <>
                      <div className={`animate-spin rounded-full h-4 w-4 border-b-2 border-white ${isRTL ? 'ml-2' : 'mr-2'}`}></div>
                      {t('countries.adding')}
                    </>
                  ) : (
                    t('countries.addCountry')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Countries;
